import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { drugRecallService } from '../services/drugRecallService'
import { DrugRecallFormData, DrugRecallSeverity } from '../types/drugRecall'
import './DrugRecallNew.css'

export function DrugRecallNew() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<DrugRecallFormData>({
    drugId: '',
    drugName: '',
    recallDate: new Date().toISOString().split('T')[0],
    severity: 'high',
    status: 'active',
    title: '',
    description: '',
    reason: '',
    actionRequired: '',
    source: 'Manual',
  })

  const createMutation = useMutation({
    mutationFn: (data: DrugRecallFormData) => drugRecallService.create(data),
    onSuccess: (recall) => {
      queryClient.invalidateQueries({ queryKey: ['drug-recalls'] })
      navigate(`/settings/drug-recalls/${recall.id}`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate(formData)
  }

  return (
    <div className="drug-recall-new-page">
      <div className="page-header">
        <h1>Create New Drug Recall</h1>
        <button className="btn-secondary" onClick={() => navigate('/settings/drug-recalls')}>
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="drug-recall-form">
        <div className="form-group">
          <label htmlFor="title">
            Title <span className="required">*</span>
          </label>
          <input
            id="title"
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            placeholder="e.g., Batch Recall - Product X"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="drugId">
              Drug ID <span className="required">*</span>
            </label>
            <input
              id="drugId"
              type="text"
              value={formData.drugId}
              onChange={(e) => setFormData({ ...formData, drugId: e.target.value })}
              required
              placeholder="Drug identifier"
            />
          </div>

          <div className="form-group">
            <label htmlFor="drugName">Drug Name</label>
            <input
              id="drugName"
              type="text"
              value={formData.drugName || ''}
              onChange={(e) => setFormData({ ...formData, drugName: e.target.value })}
              placeholder="Drug name"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="recallDate">
              Recall Date <span className="required">*</span>
            </label>
            <input
              id="recallDate"
              type="date"
              value={formData.recallDate}
              onChange={(e) => setFormData({ ...formData, recallDate: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="severity">
              Severity <span className="required">*</span>
            </label>
            <select
              id="severity"
              value={formData.severity}
              onChange={(e) => setFormData({ ...formData, severity: e.target.value as DrugRecallSeverity })}
              required
            >
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={formData.status || 'active'}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
            >
              <option value="active">Active</option>
              <option value="resolved">Resolved</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">
            Description <span className="required">*</span>
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={4}
            placeholder="Describe the recall"
          />
        </div>

        <div className="form-group">
          <label htmlFor="reason">
            Reason <span className="required">*</span>
          </label>
          <textarea
            id="reason"
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            required
            rows={3}
            placeholder="Reason for the recall"
          />
        </div>

        <div className="form-group">
          <label htmlFor="actionRequired">
            Action Required <span className="required">*</span>
          </label>
          <textarea
            id="actionRequired"
            value={formData.actionRequired}
            onChange={(e) => setFormData({ ...formData, actionRequired: e.target.value })}
            required
            rows={3}
            placeholder="What action should be taken?"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="source">Source</label>
            <input
              id="source"
              type="text"
              value={formData.source || ''}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              placeholder="e.g., HPRA, FDA, Manual"
            />
          </div>

          <div className="form-group">
            <label htmlFor="sourceReference">Source Reference</label>
            <input
              id="sourceReference"
              type="text"
              value={formData.sourceReference || ''}
              onChange={(e) => setFormData({ ...formData, sourceReference: e.target.value })}
              placeholder="Reference number or URL"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="contactInformation">Contact Information</label>
          <textarea
            id="contactInformation"
            value={formData.contactInformation || ''}
            onChange={(e) => setFormData({ ...formData, contactInformation: e.target.value })}
            rows={2}
            placeholder="Contact details for inquiries"
          />
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            placeholder="Additional notes"
          />
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/settings/drug-recalls')}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Recall'}
          </button>
        </div>
      </form>
    </div>
  )
}
